import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';

const useRoleApi = () => {
  const { t } = useTranslation();
  // İK rolü işaretini yalnız Şirket Yöneticisi / superAdmin değiştirebilir (sunucu da
  // `role.hrFlagAdminOnly` ile reddeder) — diğerlerine alan hiç gösterilmez.
  const { isEnterpriseAdmin, isSuperAdmin } = useAuth();
  const canManageHrFlag = Boolean(isEnterpriseAdmin || isSuperAdmin);

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    setFetching(true);
    api.get('/v1/roles')
      .then((res) => {
        const items = res.data?.data || [];
        setData(Array.isArray(items) ? items : []);
      })
      .finally(() => setFetching(false));
  }, [refreshTrigger]);

  const create = useCallback(async (formData) => {
    setFetching(true);
    try {
      await api.post('/v1/roles', formData);
      setRefreshTrigger((prev) => prev + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const update = useCallback(async (id, formData) => {
    setFetching(true);
    try {
      await api.put(`/v1/roles/${id}`, formData);
      setRefreshTrigger((prev) => prev + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const _delete = useCallback(async (ids) => {
    try {
      for (const id of ids) {
        await api.delete(`/v1/roles/${id}`);
      }
      setRefreshTrigger((prev) => prev + 1);
      return true;
    } catch {
      return false;
    }
  }, []);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'name', label: t('role.name'), options: { filter: false, sort: true } },
    { name: 'description', label: t('role.description'), options: { filter: false, sort: false } },
    {
      name: 'userCount',
      label: t('role.userCount'),
      options: { filter: false, sort: true },
    },
    {
      name: 'isEnterpriseAdmin',
      label: t('role.enterpriseAdmin'),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => value ? t('common.yes') : t('common.no'),
      },
    },
    // İK rolü ayrımı (2026-10-28) — bu rolün sahipleri kiracının İK'sıdır.
    {
      name: 'isHrRole',
      label: t('role.hrRole'),
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => value ? t('common.yes') : t('common.no'),
      },
    },
  ], [t]);

  const objectSchema = useMemo(() => [
    { name: 'name', label: t('role.name'), type: 'text', grid: 12, required: true },
    { name: 'description', label: t('role.description'), type: 'textarea', grid: 12 },
    {
      name: 'isHrRole',
      label: t('role.hrRole'),
      type: 'checkbox',
      grid: 12,
      hidden: !canManageHrFlag,
      description: t('role.hrRoleHint'),
    },
  ], [t, canManageHrFlag]);

  return { objectSchema, columns, data, fetching, create, update, _delete };
};

export default useRoleApi;
